import { useEffect, useState } from 'react';
import { TextWithContentModel } from '../../../model/TextWithContentModel';

interface useYoutubeVideoProps {
  content: TextWithContentModel;
  languageCode: string;
}

/**
 * @description This custom hook handles the logic of the YouTube video
 * @param0 content: The widgetState / data of the widget
 * @param1 languageCode: The language of the widget
 * @returns {JSX.Element} isVideoLoaded, thumbnailUrl, handleVideoLoad, getTitleNContent states / functions to use in the component
 */

export const useYoutubeVideo = ({ content }: useYoutubeVideoProps) => {
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState('');

  // it will be the src for the iframe
  const [videoSrc, setVideoSrc] = useState('');

  //this function is called when the video is clicked and the video is loaded
  const handleVideoLoad = () => {
    setIsVideoLoaded(true);
  };

  //this comopnent extracts the youtube video id from the videos url
  const extractNormalYoutubeVideoId = (url: string) => {
    const regex = /[?&]v=([^?&]+)/;
    const match = url.match(regex);
    return match ? match[1] : '';
  };

  //this comopnent extracts the youtube video id from the embed videos url
  const extractEmbedYoutubeVideoId = (url: string) => {
    const parts = url.split('/');
    const temp = parts[parts.length - 1].split('?')[0];
    return temp;
  };

  // this function generates the thumbnail url of the video and returns it
  const generateThumbnailUrl = (videoId: string) => {
    return `https://img.youtube.com/vi/${videoId}/0.jpg`;
  };

  useEffect(() => {
    if (content.mediaType !== 1 || !content.youTubeContent) return;

    let tempVideoId = '';

    // case of embed links
    if (content.youTubeContent.includes('/embed/')) {
      tempVideoId = extractEmbedYoutubeVideoId(content.youTubeContent.toString());
      setVideoSrc(content.youTubeContent.toString());
    }
    // case of normal links
    else {
      tempVideoId = extractNormalYoutubeVideoId(content.youTubeContent.toString());
      setVideoSrc(`https://www.youtube.com/embed/${tempVideoId}`);
    }

    setThumbnailUrl(generateThumbnailUrl(tempVideoId));
  }, [content.mediaType, content.youTubeContent]);

  return {
    isVideoLoaded,
    thumbnailUrl,
    handleVideoLoad,
    videoSrc,
  };
};
